import { SITE_NAME, SITE_URL } from "@/helper/constant";
import { t, localePrefix } from "@/helper/helper";
import { baseURL } from "@/services/api";
import axios from "axios";
import { ILocations, ILocationsGeneric } from "global-types/global.types";
import { GetServerSideProps } from "next";
import { NextSeo } from "next-seo";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import styled from "styled-components";
import Head from "next/head";

interface Props {
  locale: string;
  locations: ILocations<ILocationsGeneric[]>;
}

export default function Locations({ locale, locations }: Props) {
  const breadCrumbs = useRef<any>([]);

  useEffect(() => {
    breadCrumbs.current = [];
  });

  const { asPath } = useRouter();

  // Create breadcrumb schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: t("homePage", locale),
        item: `${SITE_URL}${localePrefix(locale)}`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: t("locations", locale),
        item: `${SITE_URL}${localePrefix(locale)}/locations`,
      },
    ],
  };

  // Create ItemList schema for locations
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: t("locations", locale),
    description: `Promusic.az ${t("locations", locale)}`,
    url: `${SITE_URL}${localePrefix(locale)}/locations`,
    numberOfItems: locations?.data?.length || 0,
    itemListElement: locations?.data?.map((location, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: `${SITE_URL}${localePrefix(locale)}/locations/${location.slug}`,
    })),
  };

  return (
    <Wrapper>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }}
        />
      </Head>
      <NextSeo
        title={`Locations | ${SITE_NAME}`}
        additionalMetaTags={[
          {
            name: "title",
            content: `Locations | ${SITE_NAME}`,
          },
        ]}
        canonical={SITE_URL + localePrefix(locale) + asPath}
        openGraph={{
          url: SITE_URL + localePrefix(locale) + asPath,
          title: "Locations" + " | " + SITE_NAME,
          siteName: SITE_URL,
        }}
      />
      <div className="about">
        <div className="about-header">
          <div className="breadcrumb">
            <Link href={`${localePrefix(locale) || "/"}`}>{t("homePage", locale)}</Link>
            <span className="breadcrumb-seperator" />
            <Link href={`${localePrefix(locale)}/locations`}>{t("locations", locale)}</Link>
          </div>
        </div>
        <div className="about-content">
          <h1 style={{ textAlign: "center", fontSize: 30 }}>
            {t("ourLocation", locale)}
          </h1>
          <div className="locationsFlex">
            {locations.data.map((item, index) => (
              <Link
                href={`${localePrefix(locale)}/locations/${item.slug}`}
                key={index.toString() + item.name}
                className="locationsFlex__item"
              >
                <img
                  alt={item.name}
                  src={item.thumbnail}
                  className="locationsFlex__item--img"
                />
                <span className="titleLocation">{item.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  .titleLocation {
    display: flex;
    justify-content: center;
    font-size: 20px;
    color: #222;
  }

  .locationsFlex {
    &__item {
      &--img {
        height: 205px;
        object-fit: cover;
      }
    }
  }
`;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  try {
    const { locale } = ctx;

    const headerData = await axios.get(baseURL + "/category", {
      headers: {
        "x-localization": locale ? locale : "az",
      },
    });
    const locations = await axios.get(baseURL + "/location", {
      headers: {
        "x-localization": locale ? locale : "az",
      },
    });

    return {
      props: {
        headerData: headerData.data,
        locations: locations.data,
        locale,
      },
    };
  } catch (error) {
    return {  
      notFound: true,
    };
  }
};
